/**
 * SRDM — Máximas e Mínimas.
 *
 * Migrado do Silverlight (GraficoDePrata/Formulas/FMLMomentum.cs, classe
 * FML.SRDM). Painel próprio, 2 linhas (SRDM + ASRDM):
 *
 *   DMZ   = IF((H+L) <= (REF(H,1)+REF(L,1)), 0, MAX(|H−REF(H,1)|, |L−REF(L,1)|))
 *   DMF   = IF((H+L) >= (REF(H,1)+REF(L,1)), 0, MAX(|H−REF(H,1)|, |L−REF(L,1)|))
 *   ADMZ  = MA(DMZ,10)     ADMF = MA(DMF,10)        (janela 10 fixa no engine)
 *   SRDM  = IF(ADMZ > ADMF, (ADMZ−ADMF)/ADMZ, IF(ADMZ = ADMF, 0, (ADMZ−ADMF)/ADMF))
 *   ASRDM = SMA(SRDM, N, 1)
 *
 * ⚠️ Quirk do legado, portado VERBATIM: o 3º argumento do SRDM tem `ADMZ = ADMF`
 * — ATRIBUIÇÃO em C#, não comparação. Os args do IF externo são avaliados da
 * esquerda para a direita, então a condição e o 2º arg ainda usam o ADMZ
 * original; já dentro do IF interno o ADMZ passa a ser o ADMF, e o ramo vira
 * (ADMF−ADMF)/ADMF. Resultado efetivo do IF interno: 0 onde ADMF ≠ 0 e NaN
 * (0/0) onde ADMF = 0. Confirmado bit-exact contra o oráculo .NET em
 * __tests__/srdm.silverlight.test.js — não "corrigir" para `==`.
 */
import {
  ref,
  add,
  sub,
  abs,
  max2,
  ma,
  div,
  gt,
  ge,
  le,
  iff,
  sma,
} from "./silverlightDsl";
import { SRDM as defaultOptions } from "./defaultOptionsForComputation";

const MA_WINDOW = 10; // fixo no engine (MA(DMZ,10) / MA(DMF,10))

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { period } = options;
    const num = (v) => (Number.isFinite(v) ? v : NaN);
    const h = data.map((d) => num(d.high));
    const l = data.map((d) => num(d.low));

    const hl = add(h, l);
    const hlPrev = add(ref(h, 1), ref(l, 1));
    const range = max2(abs(sub(h, ref(h, 1))), abs(sub(l, ref(l, 1))));

    const dmz = iff(le(hl, hlPrev), 0, range);
    const dmf = iff(ge(hl, hlPrev), 0, range);
    const admz = ma(dmz, MA_WINDOW);
    const admf = ma(dmf, MA_WINDOW);

    // IF interno com o quirk da atribuição: cond = ADMF (série de valores, não
    // 0/1) e o ramo "else" já enxerga ADMZ := ADMF.
    const inner = iff(admf, 0, div(sub(admf, admf), admf));
    const srdm = iff(gt(admz, admf), div(sub(admz, admf), admz), inner);
    const asrdm = sma(srdm, period, 1);

    return srdm.map((v, i) => {
      const srdmOut = Number.isFinite(v) ? v : undefined;
      const asrdmOut = Number.isFinite(asrdm[i]) ? asrdm[i] : undefined;
      if (srdmOut === undefined && asrdmOut === undefined) return undefined;
      return { srdm: srdmOut, asrdm: asrdmOut };
    });
  }

  calculator.undefinedLength = function () {
    // SRDM já emite desde a barra 0 (o IF interno devolve 0 no warm-up); o
    // warm-up efetivo é o do ASRDM (seed da SMA-chinesa em i = N-1).
    const { period } = options;
    return Math.max(0, period - 1);
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
